/**
 * 🍄 Arbre Mycélien — arbre de compétences UNIFIÉ, agnostique de la classe.
 *
 * Vision (2026-07-27) : la classe de départ ne sert qu'à fournir les stats
 * qui débloquent les premiers sorts de son école. Ensuite, N'IMPORTE QUEL
 * personnage peut débloquer N'IMPORTE QUEL sort si ses prérequis sont remplis :
 *   - seuils d'attributs (FOR, INT, SAG, ESP…)
 *   - affinités élémentaires (l'axe tertiaire, poussé « jusqu'au bout »)
 *   - compétences pré-requises (le `req` existant)
 *   - titres (pour les sorts les plus fous)
 *
 * Le module est PUR (aucun import de store) : l'évaluateur reçoit un contexte
 * construit par l'appelant. Les nouveaux sorts n'utilisent QUE des types,
 * statuts et éléments déjà gérés par le moteur de combat — moteur non touché.
 */

import { SKILL_TREES } from '../combat/skillTrees.js';
import { CLASSES } from './items.js';
import { TITLES } from './titles.js';
import { ELEMENTS } from '../combat/elements.js';
import { describeStatusEntry as describeCombatStatusEntry, describeSkillStatuses as describeCombatSkillStatuses } from '../combat/combatDescriptions.js';

// ── Types ────────────────────────────────────────────────────────────────

/** Prérequis d'un nœud de l'Arbre Mycélien. Tous les champs sont en ET logique. */
export interface LifeUnlock {
  level?: number;
  /** Seuils d'attributs finaux, ex. { INT: 30, ESP: 22 } */
  stats?: Record<string, number>;
  /** Seuils d'affinité élémentaire, ex. { fire: 4 } (lit affinity_fire) — l'OFFENSE */
  affinities?: Record<string, number>;
  /** Seuils de résistance élémentaire, ex. { fire: 2 } (lit resistance_fire) — la DÉFENSE.
   *  Les wards se débloquent par la résistance investie, symétrique des affinités. */
  resistances?: Record<string, number>;
  /** Compétences requises (ids, toutes exigées) */
  skills?: string[];
  /** Titres requis (ids de TITLES, tous exigés) */
  titles?: string[];
}

export interface UnlockContext {
  level: number;
  /** Stats finales du joueur — doit inclure les clés affinity_<element> */
  stats: Record<string, number>;
  /** Ids des titres débloqués */
  titles: string[];
  /** Ids des compétences déjà débloquées */
  skillsUnlocked: string[];
}

export interface UnlockMissing {
  kind: 'level' | 'stat' | 'affinity' | 'resistance' | 'skill' | 'title';
  label: string;   // libellé français prêt à afficher, ex. « INT 30 requis (18) »
  have: number | boolean;
  need: number | boolean;
  /** Présent seulement pour un prérequis de compétence : permet une navigation fiable vers son nœud. */
  skillId?: string;
}

export interface LifeNode {
  skill: any;            // définition de sort (shape skillTrees)
  sourceClass: string | null; // classe d'origine (null = Rameaux de Vie)
  voie: string;          // groupe d'affichage
  unlock: LifeUnlock;    // prérequis du nœud
}

/**
 * Source de contenu injectable. Le domaine reste pur : le client peut lui
 * fournir les registres administrables, tandis que le serveur et les scripts
 * d'équilibrage utilisent les données de référence par défaut.
 */
export interface LifeTreeContent {
  skillTrees: Record<string, any[]>;
  classes: Record<string, any>;
}

type LifeTreeContentInput = Partial<LifeTreeContent>;

function resolveLifeTreeContent(content?: LifeTreeContentInput): LifeTreeContent {
  return {
    skillTrees: content?.skillTrees || SKILL_TREES as Record<string, any[]>,
    classes: content?.classes || CLASSES as Record<string, any>
  };
}

// ── Écoles héritées ──────────────────────────────────────────────────────

/** Stat d'école par classe source (fallback des sorts sans affinity). */
const SOURCE_STAT: Record<string, string> = {
  guerrier: 'FOR', mage: 'INT', voleur: 'DEX', pretre: 'SAG', paladin: 'SAG', barde: 'CHA'
};

/**
 * Les écoles de l'Arbre = les ATTRIBUTS (plus les classes, qui sont mortes).
 * Un sort appartient à l'école de la stat qui le débloque : cohérent avec le
 * modèle « tes stats ouvrent tes sorts ».
 */
export const VOIES: Record<string, { label: string; icon: string }> = {
  FOR: { label: 'École de la Force', icon: '⚔️' },
  DEX: { label: 'École de la Dextérité', icon: '🗡️' },
  END: { label: 'École de l\'Endurance', icon: '🛡️' },
  AGI: { label: 'École de l\'Agilité', icon: '💨' },
  INT: { label: 'École de l\'Arcane', icon: '📖' },
  SAG: { label: 'École de la Foi', icon: '⚕️' },
  CHA: { label: 'École du Chant', icon: '🎵' },
  ESP: { label: 'École de l\'Esprit', icon: '🔮' },
  VIT: { label: 'École de la Vitalité', icon: '❤️' }
};

/** École d'un nœud = la stat au seuil le plus élevé de son prérequis. */
function voieOf(unlock: LifeUnlock, skill: any, classId: string | null) {
  const entries = Object.entries(unlock.stats || {});
  if (entries.length) {
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  }
  return classId ? (SOURCE_STAT[classId] || 'FOR') : mainStatOf(skill, classId || 'guerrier');
}

/** Bonus de seuil par tier : un sort de haut tier exige plus que le bloc de départ. */
const TIER_BONUS = [0, 0, 8, 18, 30]; // index = tier (1 à 4)

/** Stat principale d'un sort = la clé d'affinity au poids le plus fort. */
function mainStatOf(skill: any, classId: string) {
  const aff = skill.affinity || {};
  let best: string | null = null;
  let bestW = -Infinity;
  for (const [k, w] of Object.entries(aff)) {
    if ((w as number) > bestW) { best = k; bestW = w as number; }
  }
  return best || SOURCE_STAT[classId] || 'FOR';
}

/**
 * Dérive le prérequis d'un sort HÉRITÉ d'une classe : seuil = stat de départ
 * de la classe source (sur la stat principale du sort) + bonus de tier.
 * Ainsi, chaque classe débloque naturellement ses sorts tier 1 au départ,
 * et toute autre classe peut y accéder en investissant ses points librement.
 */
/** Affinité élémentaire requise par tier (sorts NON-physiques hérités).
 *  Tier 1 : aucune (le sort de départ de chaque école reste accessible),
 *  puis 1 / 2 / 4 points dans l'élément du sort — l'axe affinités compte. */
const TIER_AFFINITY = [0, 0, 1, 2, 4];

function deriveUnlock(skill: any, classId: string, classes: Record<string, any>): LifeUnlock {
  const stat = mainStatOf(skill, classId);
  const base = classes[classId]?.baseStats?.[stat] ?? 10;
  const tier = Math.min(4, Math.max(1, skill.tier || 1));
  const unlock: LifeUnlock = {
    level: skill.minLevel || 1,
    stats: { [stat]: base + TIER_BONUS[tier] },
    skills: skill.req ? [skill.req] : []
  };
  const el = skill.element;
  if (el && el !== 'physical' && TIER_AFFINITY[tier] > 0) {
    unlock.affinities = { [el]: TIER_AFFINITY[tier] };
  }
  return unlock;
}

// ── 🌳 Rameaux de Vie : nouveaux sorts (24) ──────────────────────────────
// Philosophie : jouer sur les ÉTATS (stun, sommeil, saignement, charme,
// barrière, brèche…) plutôt que sur la seule puissance brute.
// Types / statuts / éléments : STRICTEMENT ceux que le moteur gère déjà.

function spell(spec: any) {
  return {
    target: 'single', cost: {}, cooldown: 0, statuses: [], req: null,
    skillPoints: 1, branch: 'offense', minLevel: 1, tier: 1, ...spec
  };
}

export const LIFE_SPELLS: any[] = [
  // ── 🔥 Feu ──
  spell({ id: 'vt_cendres', name: 'Nuée de Cendres', icon: '🌋', type: 'debuff', element: 'fire',
    affinity: { INT: 0.8 }, cost: { mp: 14 }, cooldown: 3, minLevel: 3, tier: 2, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.8, duration: 3 }, { id: 'burn', chance: 0.5, duration: 3 }],
    unlock: { stats: { INT: 20 }, affinities: { fire: 2 } },
    desc: 'Un nuage brûlant qui sape les dégâts de la cible et l\'enflamme.' }),
  spell({ id: 'vt_fournaise', name: 'Cœur de Fournaise', icon: '🔥', type: 'buff', element: 'fire',
    affinity: { INT: 0.5 }, cost: { mp: 18 }, target: 'self', cooldown: 4, minLevel: 6, tier: 3, branch: 'utility',
    statuses: [{ id: 'fireAttune', chance: 1, duration: 3 }],
    unlock: { stats: { INT: 28 }, affinities: { fire: 4 }, skills: ['vt_cendres'] },
    desc: 'La chaleur intérieure attise les flammes : seuls les dégâts de FEU s\'embrasent.' }),

  // ── ❄️ Glace ──
  spell({ id: 'vt_givre', name: 'Morsure du Givre', icon: '❄️', type: 'attack', element: 'ice',
    affinity: { INT: 1.2 }, cost: { mp: 14 }, cooldown: 2, minLevel: 3, tier: 2,
    statuses: [{ id: 'freeze', chance: 0.5, duration: 2 }],
    unlock: { stats: { INT: 22 }, affinities: { ice: 2 } },
    desc: 'Attaque glacée qui ralentit la vitesse de combat de la cible.' }),
  spell({ id: 'vt_zero', name: 'Zéro Absolu', icon: '🧊', type: 'debuff', element: 'ice',
    affinity: { INT: 0.9 }, cost: { mp: 26 }, cooldown: 5, minLevel: 8, tier: 4, branch: 'control',
    statuses: [{ id: 'stun', chance: 0.6, duration: 1 }, { id: 'freeze', chance: 1, duration: 2 }],
    unlock: { stats: { INT: 34 }, affinities: { ice: 5 }, skills: ['vt_givre'] },
    desc: 'Fige la cible dans la glace : étourdie, puis ralentie.' }),

  // ── ⚡ Foudre ──
  spell({ id: 'vt_statique', name: 'Charge Statique', icon: '🌩️', type: 'debuff', element: 'lightning',
    affinity: { DEX: 0.8 }, cost: { mp: 12 }, cooldown: 3, minLevel: 3, tier: 2, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.7, duration: 2 }, { id: 'stun', chance: 0.25, duration: 1 }],
    unlock: { stats: { DEX: 20 }, affinities: { lightning: 2 } },
    desc: 'Électrise la cible : ses dégâts chutent, et elle peut être étourdie.' }),
  spell({ id: 'vt_orage', name: 'Appel de l\'Orage', icon: '⛈️', type: 'aoe', element: 'lightning',
    affinity: { INT: 1.3 }, cost: { mp: 30 }, target: 'allEnemies', cooldown: 4, minLevel: 8, tier: 4,
    statuses: [{ id: 'stun', chance: 0.3, duration: 1 }],
    unlock: { stats: { INT: 30 }, affinities: { lightning: 5 } },
    desc: 'La foudre frappe tous les ennemis et peut les étourdir.' }),

  // ── 💧 Eau ──
  spell({ id: 'vt_maree', name: 'Marée Vive', icon: '🌊', type: 'heal', element: 'water',
    affinity: { SAG: 1.2 }, cost: { mp: 14 }, target: 'ally', cooldown: 2, minLevel: 3, tier: 2, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { SAG: 20 }, affinities: { water: 2 } },
    desc: 'Soigne un allié et laisse une régénération sur 3 tours.' }),
  spell({ id: 'vt_ressac', name: 'Ressac', icon: '🌀', type: 'attack', element: 'water',
    affinity: { SAG: 1.0, INT: 0.4 }, cost: { mp: 18 }, cooldown: 3, minLevel: 6, tier: 3,
    statuses: [{ id: 'freeze', chance: 0.45, duration: 2 }],
    unlock: { stats: { SAG: 26 }, affinities: { water: 4 } },
    desc: 'Une lame de fond qui emporte la cible et ralentit sa vitesse de combat.' }),

  // ── 🌍 Terre ──
  spell({ id: 'vt_granit', name: 'Peau de Granit', icon: '🗿', type: 'buff', element: 'earth',
    affinity: { END: 0.6 }, cost: { mp: 16 }, target: 'allAllies', cooldown: 4, minLevel: 3, tier: 2, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { END: 20 }, affinities: { earth: 2 } },
    desc: 'Durcit la peau de toute l\'équipe comme le granit.' }),
  spell({ id: 'vt_faille', name: 'Faille Tellurique', icon: '🌋', type: 'aoe', element: 'earth',
    affinity: { FOR: 1.4 }, cost: { stamina: 24 }, target: 'allEnemies', cooldown: 4, minLevel: 8, tier: 4,
    statuses: [{ id: 'defDown', chance: 0.6, duration: 2 }],
    unlock: { stats: { FOR: 30 }, affinities: { earth: 5 } },
    desc: 'Le sol se déchire sous tous les ennemis et fissure leurs armures.' }),

  // ── 💨 Vent ──
  spell({ id: 'vt_zephyr', name: 'Zéphyr', icon: '🍃', type: 'buff', element: 'wind',
    affinity: { AGI: 0.6 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 3, tier: 2, branch: 'support',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 20 }, affinities: { wind: 2 } },
    desc: 'Un vent porteur accélère toute l\'équipe.' }),
  spell({ id: 'vt_bourrasque', name: 'Bourrasque Soporifique', icon: '😴', type: 'debuff', element: 'wind',
    affinity: { AGI: 0.8 }, cost: { mp: 18 }, cooldown: 4, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'sleep', chance: 0.45, duration: 2 }],
    unlock: { stats: { AGI: 26 }, affinities: { wind: 4 } },
    desc: 'Un souffle chargé de pollens qui peut endormir la cible.' }),

  // ── 🌿 Nature ──
  spell({ id: 'vt_ronces', name: 'Étreinte de Ronces', icon: '🌹', type: 'dot', element: 'nature',
    affinity: { DEX: 0.9 }, cost: { mp: 14 }, cooldown: 3, minLevel: 3, tier: 2,
    statuses: [{ id: 'poison', chance: 0.8, duration: 3 }, { id: 'bleed', chance: 0.5, duration: 3 }],
    unlock: { stats: { DEX: 22 }, affinities: { nature: 2 } },
    desc: 'Des ronces empoisonnées lacèrent la cible : poison + saignement.' }),
  spell({ id: 'vt_seve', name: 'Sève Vivifiante', icon: '🌱', type: 'heal', element: 'nature',
    affinity: { SAG: 1.4 }, cost: { mp: 26 }, target: 'allAllies', cooldown: 5, minLevel: 8, tier: 4, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { SAG: 30 }, affinities: { nature: 5 } },
    desc: 'La sève de la forêt soigne et régénère toute l\'équipe.' }),

  // ── ✨ Sacré ──
  spell({ id: 'vt_sceau', name: 'Sceau Gardien', icon: '🔯', type: 'barrier', element: 'holy',
    affinity: { SAG: 0.6 }, cost: { mp: 16 }, target: 'ally', effect: { flat: 80 }, cooldown: 4, minLevel: 3, tier: 2, branch: 'defense',
    unlock: { stats: { SAG: 22 }, affinities: { holy: 2 } },
    desc: 'Un sceau de lumière absorbe 80 dégâts sur un allié.' }),
  spell({ id: 'vt_verdict', name: 'Verdict Céleste', icon: '⚖️', type: 'attack', element: 'holy',
    affinity: { SAG: 1.1 }, cost: { mp: 38 }, cooldown: 6, minLevel: 12, tier: 4,
    statuses: [{ id: 'stun', chance: 0.3, duration: 1 }],
    unlock: { stats: { SAG: 42 }, affinities: { holy: 6 }, titles: ['dragon_slayer'] },
    desc: 'Le jugement du ciel s\'abat : dégâts sacrés massifs, peut étourdir. Réservé aux tueurs de dragons.' }),

  // ── 🌑 Ténèbres ──
  spell({ id: 'vt_cauchemar', name: 'Cauchemar', icon: '🌙', type: 'debuff', element: 'dark',
    affinity: { INT: 0.9 }, cost: { mp: 18 }, cooldown: 4, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'sleep', chance: 0.55, duration: 2 }, { id: 'atkDown', chance: 1, duration: 2 }],
    unlock: { stats: { INT: 26 }, affinities: { dark: 3 } },
    desc: 'Plonge la cible dans un sommeil noir et sape tous ses dégâts.' }),
  spell({ id: 'vt_linceul', name: 'Linceul d\'Ombre', icon: '🕸️', type: 'attack', element: 'dark',
    affinity: { INT: 1.6 }, cost: { mp: 26 }, cooldown: 4, minLevel: 8, tier: 4,
    statuses: [{ id: 'bleed', chance: 0.6, duration: 3 }, { id: 'defDown', chance: 0.5, duration: 2 }],
    unlock: { stats: { INT: 34 }, affinities: { dark: 5 }, skills: ['vt_cauchemar'] },
    desc: 'Les ombres lacèrent la cible : elle saigne et son armure se fissure.' }),

  // ── ⚔️ Voie martiale (sans affinité élémentaire) ──
  spell({ id: 'vt_volonte', name: 'Volonté de Fer', icon: '🛡️', type: 'buff', element: 'physical',
    affinity: { FOR: 0.5 }, cost: { stamina: 14 }, target: 'self', cooldown: 4, minLevel: 4, tier: 2, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }, { id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { FOR: 24, END: 18 } },
    desc: 'Corps et esprit bandés : tous les dégâts ET l\'armure renforcés.' }),
  spell({ id: 'vt_garde', name: 'Garde Parfaite', icon: '🧱', type: 'barrier', element: 'physical',
    affinity: { END: 0.8 }, cost: { stamina: 20 }, target: 'self', effect: { flat: 120 }, cooldown: 5, minLevel: 6, tier: 3, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { END: 30 } },
    desc: 'Une posture impénétrable : barrière ET armure renforcée pendant qu\'elle tient.' }),
  spell({ id: 'vt_transe', name: 'Transe du Berserker', icon: '😤', type: 'buff', element: 'physical',
    affinity: { FOR: 0.6 }, cost: { stamina: 26 }, target: 'self', cooldown: 5, minLevel: 10, tier: 4, branch: 'utility',
    statuses: [{ id: 'atkUp', chance: 1, duration: 4 }, { id: 'vitUp', chance: 1, duration: 4 }],
    unlock: { stats: { FOR: 32, AGI: 20 }, skills: ['vt_volonte'] },
    desc: 'Fureur froide : tous les dégâts et la vitesse de combat accrus.' }),

  // ── 🔮 Voie de l'Esprit (ESP — la nouvelle stat à l'honneur) ──
  spell({ id: 'vt_clarte', name: 'Clarté d\'Esprit', icon: '💠', type: 'buff', element: 'physical',
    affinity: { ESP: 0.5 }, cost: { mp: 12 }, target: 'self', cooldown: 4, minLevel: 4, tier: 2, branch: 'utility',
    statuses: [{ id: 'intUp', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 22 } },
    desc: 'L\'esprit s\'aiguise : les sorts d\'Intelligence frappent plus fort.' }),
  spell({ id: 'vt_communion', name: 'Communion Psychique', icon: '🧠', type: 'buff', element: 'physical',
    affinity: { ESP: 0.6 }, cost: { mp: 24 }, target: 'allAllies', cooldown: 5, minLevel: 8, tier: 3, branch: 'support',
    statuses: [{ id: 'intUp', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 34 }, skills: ['vt_clarte'] },
    desc: 'Relie les esprits : les sorts d\'Intelligence de TOUTE l\'équipe frappent plus fort.' }),
  spell({ id: 'vt_eveil', name: 'Éveil de l\'Âme', icon: '🕊️', type: 'revive', element: 'holy',
    affinity: { SAG: 1.5 }, cost: { mp: 44 }, target: 'ally', effect: { pctHp: 0.5 }, cooldown: 6, minLevel: 12, tier: 4, branch: 'support',
    unlock: { stats: { ESP: 40, SAG: 30 }, affinities: { holy: 4 }, titles: ['healer_master'] },
    desc: 'Rappelle une âme : ressuscite un allié avec 50% de ses PV. Réservé aux maîtres soigneurs.' }),

  // ── 💖 Charme ──
  spell({ id: 'vt_serenade', name: 'Sérénade Fatale', icon: '🎻', type: 'debuff', element: 'holy',
    affinity: { CHA: 1.4 }, cost: { mp: 30 }, cooldown: 5, minLevel: 10, tier: 4, branch: 'control',
    statuses: [{ id: 'charmed', chance: 0.65, duration: 2 }, { id: 'atkDown', chance: 1, duration: 2 }],
    unlock: { stats: { CHA: 34 }, affinities: { holy: 3 } },
    desc: 'Une mélodie envoutante : même en résistant au charme, la cible en sort les bras lourds.' }),

  // ═══ 🛡️ ÉCOLE DE L'ENDURANCE — le rempart (wards élémentaires & protection) ═══
  spell({ id: 'vt_egide', name: 'Égide de Fer', icon: '🛡️', type: 'barrier', element: 'physical',
    affinity: { END: 0.7 }, cost: { stamina: 12 }, target: 'self', effect: { flat: 100 }, cooldown: 4, minLevel: 2, tier: 1, branch: 'defense',
    unlock: { stats: { END: 16 } },
    desc: 'Lève une garde d\'acier qui absorbe les coups.' }),
  spell({ id: 'vt_carapace', name: 'Carapace', icon: '🐢', type: 'buff', element: 'physical',
    affinity: { END: 0.5 }, cost: { stamina: 10 }, target: 'self', cooldown: 4, minLevel: 2, tier: 1, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { END: 18 } },
    desc: 'Se recroqueville derrière une armure vivante.' }),
  spell({ id: 'vt_mur_boucliers', name: 'Mur de Boucliers', icon: '🧱', type: 'barrier', element: 'physical',
    affinity: { END: 0.6 }, cost: { stamina: 18 }, target: 'allAllies', effect: { flat: 60 }, cooldown: 5, minLevel: 4, tier: 2, branch: 'defense',
    unlock: { stats: { END: 22 }, skills: ['vt_egide'] },
    desc: 'La formation serrée protège toute l\'équipe.' }),
  spell({ id: 'vt_ward_feu', name: 'Écaille de Salamandre', icon: '🦎', type: 'buff', element: 'fire',
    affinity: { END: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 4, tier: 2, branch: 'defense',
    statuses: [{ id: 'fireWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 20 }, resistances: { fire: 2 } },
    desc: 'Des écailles ignifuges couvrent l\'équipe contre le Feu.' }),
  spell({ id: 'vt_ward_glace', name: 'Manteau de Givre', icon: '🧥', type: 'buff', element: 'ice',
    affinity: { END: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 4, tier: 2, branch: 'defense',
    statuses: [{ id: 'iceWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 20 }, resistances: { ice: 2 } },
    desc: 'Un manteau isolant qui émousse la morsure de la Glace.' }),
  spell({ id: 'vt_ward_eau', name: 'Égide des Marées', icon: '🌊', type: 'buff', element: 'water',
    affinity: { END: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 4, tier: 2, branch: 'defense',
    statuses: [{ id: 'waterWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 20 }, resistances: { water: 2 } },
    desc: 'Le courant se brise sur l\'équipe au lieu de l\'emporter.' }),
  spell({ id: 'vt_ward_terre', name: 'Assise Tellurique', icon: '⛰️', type: 'buff', element: 'earth',
    affinity: { END: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'earthWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 22 }, resistances: { earth: 2 } },
    desc: 'L\'équipe s\'ancre au sol : la Terre ne peut la briser.' }),
  spell({ id: 'vt_ward_nature', name: 'Écorce Vive', icon: '🪵', type: 'buff', element: 'nature',
    affinity: { END: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'natureWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 22 }, resistances: { nature: 2 } },
    desc: 'Une écorce vivante qui repousse poisons et ronces.' }),
  spell({ id: 'vt_second_souffle', name: 'Second Souffle', icon: '🫁', type: 'heal', element: 'physical',
    affinity: { END: 1.0 }, cost: { stamina: 16 }, target: 'self', cooldown: 3, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'regen', chance: 1, duration: 2 }],
    unlock: { stats: { END: 24 } },
    desc: 'Le tank reprend son souffle et referme ses plaies.' }),
  spell({ id: 'vt_provocation', name: 'Défi du Colosse', icon: '📢', type: 'debuff', element: 'physical',
    affinity: { END: 0.6 }, cost: { stamina: 18 }, target: 'allEnemies', cooldown: 4, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.7, duration: 2 }],
    unlock: { stats: { END: 28 }, skills: ['vt_carapace'] },
    desc: 'Un défi rugissant qui fait trembler les bras ennemis.' }),
  spell({ id: 'vt_cri_ralliement', name: 'Cri de Ralliement', icon: '🚩', type: 'buff', element: 'physical',
    affinity: { END: 0.5 }, cost: { stamina: 20 }, target: 'allAllies', cooldown: 5, minLevel: 7, tier: 3, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { END: 30 } },
    desc: 'Le rempart galvanise les siens : leurs forces reviennent.' }),
  spell({ id: 'vt_sentinelle', name: 'Sentinelle', icon: '🗿', type: 'barrier', element: 'physical',
    affinity: { END: 0.8 }, cost: { stamina: 22 }, target: 'ally', effect: { flat: 150 }, cooldown: 5, minLevel: 7, tier: 3, branch: 'defense',
    unlock: { stats: { END: 30 }, skills: ['vt_mur_boucliers'] },
    desc: 'Se dresse devant un allié et encaisse à sa place.' }),
  spell({ id: 'vt_represailles', name: 'Posture de Représailles', icon: '⚒️', type: 'buff', element: 'physical',
    affinity: { END: 0.5 }, cost: { stamina: 22 }, target: 'self', cooldown: 5, minLevel: 8, tier: 3, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }, { id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { END: 26, FOR: 20 } },
    desc: 'Encaisser pour mieux rendre : garde ET riposte renforcées.' }),
  spell({ id: 'vt_voeu_gardien', name: 'Vœu du Gardien', icon: '🛐', type: 'buff', element: 'holy',
    affinity: { END: 0.5 }, cost: { mp: 26 }, target: 'ally', cooldown: 6, minLevel: 9, tier: 4, branch: 'support',
    statuses: [{ id: 'reviveMark', chance: 1, duration: 999 }],
    unlock: { stats: { END: 36 }, resistances: { holy: 2 } },
    desc: 'Jure de protéger un allié : la mort elle-même devra attendre.' }),
  spell({ id: 'vt_bastion', name: 'Bastion Élémentaire', icon: '🏰', type: 'buff', element: 'physical',
    affinity: { END: 0.5 }, cost: { mp: 30 }, target: 'allAllies', cooldown: 6, minLevel: 10, tier: 4, branch: 'defense',
    statuses: [{ id: 'allWard', chance: 1, duration: 3 }],
    unlock: { stats: { END: 34 }, skills: ['vt_ward_feu'] },
    desc: 'Une forteresse invisible contre TOUS les éléments.' }),
  spell({ id: 'vt_forteresse', name: 'Forteresse Vivante', icon: '🏯', type: 'barrier', element: 'physical',
    affinity: { END: 1.0 }, cost: { stamina: 30 }, target: 'self', effect: { flat: 200 }, cooldown: 6, minLevel: 11, tier: 4, branch: 'defense',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { END: 38 }, skills: ['vt_sentinelle'] },
    desc: 'Le corps devient citadelle vivante : énorme barrière qui, en plus, se répare seule.' }),

  // ═══ 💨 ÉCOLE DE L'AGILITÉ — vitesse, vent & foudre ═══
  spell({ id: 'vt_pas_eclair', name: 'Pas de l\'Éclair', icon: '👟', type: 'buff', element: 'physical',
    affinity: { AGI: 0.5 }, cost: { stamina: 8 }, target: 'self', cooldown: 3, minLevel: 2, tier: 1, branch: 'utility',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 16 } },
    desc: 'Les jambes s\'allègent : la jauge d\'action file.' }),
  spell({ id: 'vt_frappe_sonique', name: 'Frappe Sonique', icon: '💥', type: 'attack', element: 'wind',
    affinity: { AGI: 1.2 }, cost: { stamina: 10 }, cooldown: 2, minLevel: 2, tier: 1,
    unlock: { stats: { AGI: 18 } },
    desc: 'Un coup si rapide que l\'air claque.' }),
  spell({ id: 'vt_zigzag', name: 'Course Zigzag', icon: '〽️', type: 'debuff', element: 'physical',
    affinity: { AGI: 0.7 }, cost: { stamina: 12 }, cooldown: 3, minLevel: 3, tier: 2, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.8, duration: 2 }],
    unlock: { stats: { AGI: 20 } },
    desc: 'Des feintes si vives que la cible n\'ose plus frapper.' }),
  spell({ id: 'vt_ward_vent', name: 'Voile de Zéphyr', icon: '🪁', type: 'buff', element: 'wind',
    affinity: { AGI: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 4, tier: 2, branch: 'defense',
    statuses: [{ id: 'windWard', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 20 }, resistances: { wind: 2 } },
    desc: 'Le vent glisse sur l\'équipe au lieu de la faucher.' }),
  spell({ id: 'vt_croc_foudre', name: 'Croc de Foudre', icon: '🌩️', type: 'attack', element: 'lightning',
    affinity: { AGI: 1.1, DEX: 0.4 }, cost: { stamina: 14 }, cooldown: 3, minLevel: 4, tier: 2,
    statuses: [{ id: 'stun', chance: 0.2, duration: 1 }],
    unlock: { stats: { AGI: 22 }, affinities: { lightning: 2 } },
    desc: 'Une morsure électrique plus prompte que l\'œil.' }),
  spell({ id: 'vt_ward_foudre', name: 'Cage de Faraday', icon: '🗼', type: 'buff', element: 'lightning',
    affinity: { AGI: 0.4 }, cost: { mp: 14 }, target: 'allAllies', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'lightningWard', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 22 }, resistances: { lightning: 2 } },
    desc: 'La foudre se perd autour de l\'équipe sans jamais la toucher.' }),
  spell({ id: 'vt_poursuite', name: 'Poursuite Implacable', icon: '🏃', type: 'attack', element: 'physical',
    affinity: { AGI: 2.0, FOR: 0.6 }, cost: { stamina: 14 }, cooldown: 2, minLevel: 5, tier: 2,
    unlock: { stats: { AGI: 24 } },
    desc: 'Aucune fuite possible : la lame suit le mouvement.' }),
  spell({ id: 'vt_second_vent', name: 'Second Vent', icon: '🍃', type: 'heal', element: 'wind',
    affinity: { AGI: 1.0 }, cost: { stamina: 16 }, target: 'self', cooldown: 3, minLevel: 6, tier: 3, branch: 'support',
    statuses: [{ id: 'vitUp', chance: 1, duration: 2 }],
    unlock: { stats: { AGI: 26 }, affinities: { wind: 2 } },
    desc: 'Une bouffée d\'air frais qui remet en course.' }),
  spell({ id: 'vt_reflexes', name: 'Réflexes Éclairs', icon: '⚡', type: 'buff', element: 'physical',
    affinity: { AGI: 0.5 }, cost: { stamina: 18 }, target: 'self', cooldown: 5, minLevel: 7, tier: 3, branch: 'utility',
    statuses: [{ id: 'vitUp', chance: 1, duration: 4 }],
    unlock: { stats: { AGI: 28 }, skills: ['vt_pas_eclair'] },
    desc: 'Le monde ralentit ; toi, non.' }),
  spell({ id: 'vt_vent_paralysant', name: 'Souffle Paralysant', icon: '🌬️', type: 'debuff', element: 'wind',
    affinity: { AGI: 0.8 }, cost: { mp: 20 }, cooldown: 4, minLevel: 7, tier: 3, branch: 'control',
    statuses: [{ id: 'stun', chance: 0.4, duration: 1 }],
    unlock: { stats: { AGI: 28 }, affinities: { wind: 3 } },
    desc: 'Un souffle qui coupe net l\'élan de la cible.' }),
  spell({ id: 'vt_tempo', name: 'Tempo Fulgurant', icon: '🥁', type: 'buff', element: 'physical',
    affinity: { AGI: 0.5 }, cost: { stamina: 22 }, target: 'allAllies', cooldown: 5, minLevel: 8, tier: 3, branch: 'support',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }, { id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 30 } },
    desc: 'Toute l\'équipe cale son pas sur un rythme effréné.' }),
  spell({ id: 'vt_tourbillon', name: 'Tourbillon Aveuglant', icon: '🌪️', type: 'debuff', element: 'wind',
    affinity: { AGI: 0.9 }, cost: { mp: 26 }, target: 'allEnemies', cooldown: 5, minLevel: 9, tier: 4, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.7, duration: 2 }],
    unlock: { stats: { AGI: 32 }, affinities: { wind: 5 } },
    desc: 'Un mur de poussière et de vent qui égare tous les coups ennemis.' }),
  spell({ id: 'vt_lames_cyclone', name: 'Lames du Cyclone', icon: '🌀', type: 'aoe', element: 'wind',
    affinity: { AGI: 1.5 }, cost: { stamina: 26 }, target: 'allEnemies', cooldown: 4, minLevel: 10, tier: 4,
    statuses: [{ id: 'bleed', chance: 0.4, duration: 3 }],
    unlock: { stats: { AGI: 34 }, affinities: { wind: 4 } },
    desc: 'Le cyclone porte cent lames invisibles.' }),
  spell({ id: 'vt_chaine_eclairs', name: 'Chaîne d\'Éclairs', icon: '⛓️', type: 'aoe', element: 'lightning',
    affinity: { AGI: 1.2, INT: 0.5 }, cost: { mp: 30 }, target: 'allEnemies', cooldown: 5, minLevel: 11, tier: 4,
    statuses: [{ id: 'stun', chance: 0.25, duration: 1 }],
    unlock: { stats: { AGI: 36 }, affinities: { lightning: 5 }, skills: ['vt_croc_foudre'] },
    desc: 'La foudre saute de cible en cible sans jamais s\'éteindre.' }),

  // ═══ 🔮 ÉCOLE DE L'ESPRIT — harmonisations & pouvoirs psychiques ═══
  spell({ id: 'vt_meditation', name: 'Méditation', icon: '🧘', type: 'buff', element: 'physical',
    affinity: { ESP: 0.5 }, cost: { mp: 8 }, target: 'self', cooldown: 3, minLevel: 2, tier: 1, branch: 'utility',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 16 } },
    desc: 'Le calme intérieur répare le corps.' }),
  spell({ id: 'vt_lien_vital', name: 'Lien Vital', icon: '🔗', type: 'heal', element: 'physical',
    affinity: { ESP: 1.3 }, cost: { mp: 14 }, target: 'ally', cooldown: 2, minLevel: 3, tier: 2, branch: 'support',
    unlock: { stats: { ESP: 20 } },
    desc: 'Transfuse l\'énergie de l\'esprit vers le corps d\'un allié.' }),
  spell({ id: 'vt_harmonie_givre', name: 'Harmonie du Givre', icon: '❄️', type: 'buff', element: 'ice',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 4, tier: 2, branch: 'utility',
    statuses: [{ id: 'iceAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 18 }, affinities: { ice: 1 } },
    desc: 'Accorde l\'âme d\'un allié à la Glace : ses sorts de Glace mordent plus fort.' }),
  spell({ id: 'vt_harmonie_foudre', name: 'Harmonie de la Foudre', icon: '⚡', type: 'buff', element: 'lightning',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 4, tier: 2, branch: 'utility',
    statuses: [{ id: 'lightningAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 18 }, affinities: { lightning: 1 } },
    desc: 'Accorde l\'âme d\'un allié à la Foudre.' }),
  spell({ id: 'vt_harmonie_eaux', name: 'Harmonie des Eaux', icon: '💧', type: 'buff', element: 'water',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 4, tier: 2, branch: 'utility',
    statuses: [{ id: 'waterAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 18 }, affinities: { water: 1 } },
    desc: 'Accorde l\'âme d\'un allié à l\'Eau.' }),
  spell({ id: 'vt_harmonie_terre', name: 'Harmonie de la Terre', icon: '🌍', type: 'buff', element: 'earth',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 5, tier: 2, branch: 'utility',
    statuses: [{ id: 'earthAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 20 }, affinities: { earth: 1 } },
    desc: 'Accorde l\'âme d\'un allié à la Terre.' }),
  spell({ id: 'vt_harmonie_vents', name: 'Harmonie des Vents', icon: '💨', type: 'buff', element: 'wind',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 5, tier: 2, branch: 'utility',
    statuses: [{ id: 'windAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 20 }, affinities: { wind: 1 } },
    desc: 'Accorde l\'âme d\'un allié au Vent.' }),
  spell({ id: 'vt_harmonie_sauvage', name: 'Harmonie Sauvage', icon: '🌿', type: 'buff', element: 'nature',
    affinity: { ESP: 0.4 }, cost: { mp: 14 }, target: 'ally', cooldown: 4, minLevel: 5, tier: 2, branch: 'utility',
    statuses: [{ id: 'natureAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 20 }, affinities: { nature: 1 } },
    desc: 'Accorde l\'âme d\'un allié à la Nature.' }),
  spell({ id: 'vt_onde_psychique', name: 'Onde Psychique', icon: '🌀', type: 'attack', element: 'dark',
    affinity: { ESP: 1.4 }, cost: { mp: 18 }, cooldown: 3, minLevel: 6, tier: 3,
    unlock: { stats: { ESP: 26 }, affinities: { dark: 2 } },
    desc: 'Une lame de pensée pure qui traverse les armures de l\'esprit.' }),
  spell({ id: 'vt_harmonie_celeste', name: 'Harmonie Céleste', icon: '✨', type: 'buff', element: 'holy',
    affinity: { ESP: 0.4 }, cost: { mp: 18 }, target: 'ally', cooldown: 4, minLevel: 7, tier: 3, branch: 'utility',
    statuses: [{ id: 'holyAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 24 }, affinities: { holy: 2 } },
    desc: 'Accorde l\'âme d\'un allié au Sacré.' }),
  spell({ id: 'vt_harmonie_ombres', name: 'Harmonie des Ombres', icon: '🌑', type: 'buff', element: 'dark',
    affinity: { ESP: 0.4 }, cost: { mp: 18 }, target: 'ally', cooldown: 4, minLevel: 7, tier: 3, branch: 'utility',
    statuses: [{ id: 'darkAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 24 }, affinities: { dark: 2 } },
    desc: 'Accorde l\'âme d\'un allié aux Ténèbres.' }),
  spell({ id: 'vt_echo_mental', name: 'Écho Mental', icon: '🧠', type: 'debuff', element: 'dark',
    affinity: { ESP: 0.9 }, cost: { mp: 22 }, cooldown: 4, minLevel: 8, tier: 3, branch: 'control',
    statuses: [{ id: 'sleep', chance: 0.4, duration: 2 }, { id: 'atkDown', chance: 0.8, duration: 2 }],
    unlock: { stats: { ESP: 30 }, affinities: { dark: 3 } },
    desc: 'Ses propres pensées se retournent contre elle.' }),
  spell({ id: 'vt_transcendance', name: 'Transcendance', icon: '🌠', type: 'buff', element: 'physical',
    affinity: { ESP: 0.6 }, cost: { mp: 30 }, target: 'self', cooldown: 6, minLevel: 10, tier: 4, branch: 'utility',
    statuses: [{ id: 'intUp', chance: 1, duration: 4 }, { id: 'vitUp', chance: 1, duration: 4 }, { id: 'regen', chance: 1, duration: 4 }],
    unlock: { stats: { ESP: 38 }, skills: ['vt_meditation'] },
    desc: 'L\'esprit dépasse la chair : tout s\'accélère, tout guérit.' }),
  spell({ id: 'vt_grande_harmonie', name: 'Grande Harmonie', icon: '🌈', type: 'buff', element: 'physical',
    affinity: { ESP: 0.6 }, cost: { mp: 36 }, target: 'self', cooldown: 6, minLevel: 12, tier: 4, branch: 'utility',
    statuses: [{ id: 'allAttune', chance: 1, duration: 3 }],
    unlock: { stats: { ESP: 42 }, skills: ['vt_harmonie_celeste', 'vt_harmonie_ombres'] },
    desc: 'L\'âme résonne avec TOUS les éléments à la fois.' }),

  // ═══ ❤️ ÉCOLE DE LA VITALITÉ — le sang, la chair, la survie ═══
  spell({ id: 'vt_vigueur', name: 'Vigueur du Sanglier', icon: '🐗', type: 'heal', element: 'physical',
    affinity: { VIT: 1.2 }, cost: { stamina: 10 }, target: 'self', cooldown: 2, minLevel: 2, tier: 1, branch: 'support',
    unlock: { stats: { VIT: 16 } },
    desc: 'La vitalité brute referme les plaies.' }),
  spell({ id: 'vt_sang_chaud', name: 'Sang Chaud', icon: '🩸', type: 'buff', element: 'physical',
    affinity: { VIT: 0.5 }, cost: { stamina: 10 }, target: 'self', cooldown: 4, minLevel: 2, tier: 1, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 4 }],
    unlock: { stats: { VIT: 18 } },
    desc: 'Le sang bouillonne et régénère sans relâche.' }),
  spell({ id: 'vt_peau_dure', name: 'Peau Dure', icon: '🦏', type: 'buff', element: 'physical',
    affinity: { VIT: 0.5 }, cost: { stamina: 12 }, target: 'self', cooldown: 4, minLevel: 3, tier: 2, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { VIT: 20 } },
    desc: 'Le cuir s\'épaissit à chaque cicatrice.' }),
  spell({ id: 'vt_coeur_battant', name: 'Cœur Battant', icon: '💓', type: 'heal', element: 'physical',
    affinity: { VIT: 1.1 }, cost: { stamina: 14 }, target: 'ally', cooldown: 3, minLevel: 4, tier: 2, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 2 }],
    unlock: { stats: { VIT: 22 } },
    desc: 'Partage son propre battement de cœur avec un allié.' }),
  spell({ id: 'vt_robustesse', name: 'Robustesse', icon: '🌳', type: 'buff', element: 'nature',
    affinity: { VIT: 0.5 }, cost: { stamina: 16 }, target: 'self', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'defUp', chance: 1, duration: 3 }, { id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { VIT: 24 }, resistances: { nature: 1 } },
    desc: 'Solide comme un chêne, vivace comme ses racines.' }),
  spell({ id: 'vt_rage_sang', name: 'Rage du Sang', icon: '😤', type: 'buff', element: 'physical',
    affinity: { VIT: 0.5 }, cost: { stamina: 18 }, target: 'self', cooldown: 5, minLevel: 6, tier: 3, branch: 'utility',
    statuses: [{ id: 'atkUp', chance: 1, duration: 3 }, { id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { VIT: 26 }, skills: ['vt_sang_chaud'] },
    desc: 'La fureur du sang nourrit les coups ET les chairs.' }),
  spell({ id: 'vt_mur_chair', name: 'Mur de Chair', icon: '🧟', type: 'barrier', element: 'physical',
    affinity: { VIT: 0.9 }, cost: { stamina: 20 }, target: 'self', effect: { flat: 120 }, cooldown: 5, minLevel: 7, tier: 3, branch: 'defense',
    unlock: { stats: { VIT: 28 } },
    desc: 'Le corps lui-même devient bouclier.' }),
  spell({ id: 'vt_fureur_vitale', name: 'Fureur Vitale', icon: '❤️‍🔥', type: 'buff', element: 'physical',
    affinity: { VIT: 0.5 }, cost: { stamina: 22 }, target: 'allAllies', cooldown: 5, minLevel: 8, tier: 3, branch: 'support',
    statuses: [{ id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { VIT: 30 } },
    desc: 'La vie déborde et enflamme l\'ardeur de tous.' }),
  spell({ id: 'vt_vitalite_partagee', name: 'Vitalité Partagée', icon: '💞', type: 'heal', element: 'physical',
    affinity: { VIT: 1.3 }, cost: { stamina: 26 }, target: 'allAllies', cooldown: 5, minLevel: 10, tier: 4, branch: 'support',
    unlock: { stats: { VIT: 34 }, skills: ['vt_coeur_battant'] },
    desc: 'Une source de vie jaillit pour toute l\'équipe.' }),
  spell({ id: 'vt_instinct_survie', name: 'Instinct de Survie', icon: '🔥', type: 'buff', element: 'physical',
    affinity: { VIT: 0.5 }, cost: { stamina: 28 }, target: 'self', cooldown: 6, minLevel: 11, tier: 4, branch: 'defense',
    statuses: [{ id: 'reviveMark', chance: 1, duration: 999 }],
    unlock: { stats: { VIT: 38 } },
    desc: 'Ce corps refuse simplement de mourir.' }),

  // ═══ ⚔️ ÉCOLE DE LA FORCE — compléments élémentaires & brutalité ═══
  spell({ id: 'vt_dechirure', name: 'Déchirure', icon: '🩸', type: 'attack', element: 'physical',
    affinity: { FOR: 1.3 }, cost: { stamina: 10 }, cooldown: 2, minLevel: 2, tier: 1,
    statuses: [{ id: 'bleed', chance: 0.7, duration: 3 }],
    unlock: { stats: { FOR: 18 } },
    desc: 'Un coup qui ouvre et laisse saigner.' }),
  spell({ id: 'vt_brise_roc', name: 'Brise-Roc', icon: '⛏️', type: 'attack', element: 'earth',
    affinity: { FOR: 1.6 }, cost: { stamina: 14 }, cooldown: 3, minLevel: 4, tier: 2,
    statuses: [{ id: 'defDown', chance: 0.4, duration: 2 }],
    unlock: { stats: { FOR: 22 }, affinities: { earth: 2 } },
    desc: 'Ce qui brise la pierre brise aussi les armures.' }),
  spell({ id: 'vt_marteau_lave', name: 'Marteau de Lave', icon: '🌋', type: 'attack', element: 'fire',
    affinity: { FOR: 1.5 }, cost: { stamina: 16 }, cooldown: 3, minLevel: 5, tier: 2,
    statuses: [{ id: 'burn', chance: 0.5, duration: 3 }],
    unlock: { stats: { FOR: 24 }, affinities: { fire: 2 } },
    desc: 'Le métal en fusion s\'écrase et embrase.' }),
  spell({ id: 'vt_cri_antique', name: 'Cri de Guerre Antique', icon: '🦁', type: 'buff', element: 'physical',
    affinity: { FOR: 0.5 }, cost: { stamina: 18 }, target: 'allAllies', cooldown: 5, minLevel: 6, tier: 3, branch: 'support',
    statuses: [{ id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { FOR: 26 } },
    desc: 'Le rugissement des anciens champions habite ce cri.' }),
  spell({ id: 'vt_onde_choc', name: 'Onde de Choc', icon: '💢', type: 'aoe', element: 'physical',
    affinity: { FOR: 1.2 }, cost: { stamina: 22 }, target: 'allEnemies', cooldown: 4, minLevel: 7, tier: 3,
    statuses: [{ id: 'stun', chance: 0.2, duration: 1 }],
    unlock: { stats: { FOR: 28 } },
    desc: 'Le sol tremble, les rangs ennemis vacillent.' }),
  spell({ id: 'vt_lame_tempete', name: 'Lame de la Tempête', icon: '🌩️', type: 'attack', element: 'lightning',
    affinity: { FOR: 1.4, DEX: 0.4 }, cost: { stamina: 20 }, cooldown: 3, minLevel: 8, tier: 3,
    unlock: { stats: { FOR: 30 }, affinities: { lightning: 3 } },
    desc: 'L\'orage descend le long de la lame.' }),
  spell({ id: 'vt_seisme', name: 'Séisme', icon: '🏔️', type: 'aoe', element: 'earth',
    affinity: { FOR: 1.7 }, cost: { stamina: 28 }, target: 'allEnemies', cooldown: 5, minLevel: 10, tier: 4,
    statuses: [{ id: 'stun', chance: 0.25, duration: 1 }],
    unlock: { stats: { FOR: 38 }, affinities: { earth: 5 }, skills: ['vt_brise_roc'] },
    desc: 'La terre s\'ouvre sous tous les ennemis à la fois.' }),
  spell({ id: 'vt_colere_titan', name: 'Colère du Titan', icon: '🗿', type: 'attack', element: 'physical',
    affinity: { FOR: 2.6 }, cost: { stamina: 32 }, cooldown: 5, minLevel: 12, tier: 4,
    statuses: [{ id: 'defDown', chance: 0.7, duration: 2 }],
    unlock: { stats: { FOR: 42 }, skills: ['vt_onde_choc'] },
    desc: 'Un seul coup, pesant comme une montagne : il écrase et fissure durablement l\'armure.' }),

  // ═══ 🗡️ ÉCOLE DE LA DEXTÉRITÉ — précision élémentaire & combos ═══
  spell({ id: 'vt_piqure', name: 'Piqûre Toxique', icon: '🦂', type: 'dot', element: 'nature',
    affinity: { DEX: 1.0 }, cost: { stamina: 10 }, cooldown: 3, minLevel: 2, tier: 1,
    statuses: [{ id: 'poison', chance: 0.8, duration: 3 }],
    unlock: { stats: { DEX: 18 } },
    desc: 'Une piqûre discrète au venin tenace.' }),
  spell({ id: 'vt_lame_gel', name: 'Lame de Gel', icon: '🗡️', type: 'attack', element: 'ice',
    affinity: { DEX: 1.3 }, cost: { stamina: 12 }, cooldown: 2, minLevel: 4, tier: 2,
    statuses: [{ id: 'freeze', chance: 0.4, duration: 2 }],
    unlock: { stats: { DEX: 20 }, affinities: { ice: 2 } },
    desc: 'Une entaille si froide qu\'elle engourdit.' }),
  spell({ id: 'vt_fleche_percante', name: 'Flèche Perçante', icon: '🏹', type: 'attack', element: 'physical',
    affinity: { DEX: 2.4 }, cost: { stamina: 14 }, cooldown: 3, minLevel: 4, tier: 2,
    statuses: [{ id: 'defDown', chance: 0.5, duration: 2 }],
    unlock: { stats: { DEX: 22 } },
    desc: 'Traverse l\'armure comme du parchemin.' }),
  spell({ id: 'vt_fleche_vent', name: 'Flèche de Vent', icon: '🎯', type: 'attack', element: 'wind',
    affinity: { DEX: 1.3, AGI: 0.3 }, cost: { stamina: 14 }, cooldown: 2, minLevel: 5, tier: 2,
    unlock: { stats: { DEX: 24 }, affinities: { wind: 2 } },
    desc: 'Le vent lui-même porte le trait au but.' }),
  spell({ id: 'vt_marque_ombre', name: 'Marque d\'Ombre', icon: '🌑', type: 'debuff', element: 'dark',
    affinity: { DEX: 0.7 }, cost: { mp: 16 }, cooldown: 3, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'darkBrand', chance: 0.9, duration: 3 }],
    unlock: { stats: { DEX: 26 }, affinities: { dark: 2 } },
    desc: 'Grave une marque qui EXPOSE la cible aux Ténèbres — prépare le combo.' }),
  spell({ id: 'vt_baiser_ombre', name: 'Baiser de l\'Ombre', icon: '💋', type: 'attack', element: 'dark',
    affinity: { DEX: 1.4 }, cost: { stamina: 18 }, cooldown: 3, minLevel: 7, tier: 3,
    statuses: [{ id: 'atkDown', chance: 0.5, duration: 2 }],
    unlock: { stats: { DEX: 28 }, affinities: { dark: 3 } },
    desc: 'Une caresse ténébreuse qui vole la force.' }),
  spell({ id: 'vt_camouflage', name: 'Camouflage', icon: '🥷', type: 'buff', element: 'physical',
    affinity: { DEX: 0.5 }, cost: { stamina: 18 }, target: 'self', cooldown: 5, minLevel: 7, tier: 3, branch: 'utility',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }, { id: 'atkUp', chance: 1, duration: 3 }],
    unlock: { stats: { DEX: 28 } },
    desc: 'Disparaître, se placer, frapper au moment parfait.' }),
  spell({ id: 'vt_eventail_lames', name: 'Éventail de Lames', icon: '🪭', type: 'aoe', element: 'physical',
    affinity: { DEX: 1.2 }, cost: { stamina: 24 }, target: 'allEnemies', cooldown: 4, minLevel: 9, tier: 3,
    statuses: [{ id: 'bleed', chance: 0.4, duration: 3 }],
    unlock: { stats: { DEX: 30 } },
    desc: 'Un arc de lames qui fauche tous les rangs.' }),
  spell({ id: 'vt_point_paralysie', name: 'Point de Paralysie', icon: '🎯', type: 'debuff', element: 'physical',
    affinity: { DEX: 0.8 }, cost: { stamina: 22 }, cooldown: 5, minLevel: 10, tier: 4, branch: 'control',
    statuses: [{ id: 'stun', chance: 0.5, duration: 1 }],
    unlock: { stats: { DEX: 34 } },
    desc: 'Un doigt précis sur le nerf exact.' }),
  spell({ id: 'vt_double_ombre', name: 'Double Frappe d\'Ombre', icon: '👥', type: 'attack', element: 'dark',
    affinity: { DEX: 1.8 }, cost: { stamina: 26 }, cooldown: 4, minLevel: 11, tier: 4,
    statuses: [{ id: 'bleed', chance: 0.5, duration: 3 }],
    unlock: { stats: { DEX: 32 }, affinities: { dark: 4 }, skills: ['vt_marque_ombre'] },
    desc: 'Deux silhouettes frappent — une seule est réelle.' }),

  // ═══ 📖 ÉCOLE DE L'ARCANE — compléments eau/terre/nature & combos de marque ═══
  spell({ id: 'vt_geyser', name: 'Geyser', icon: '⛲', type: 'attack', element: 'water',
    affinity: { INT: 1.4 }, cost: { mp: 14 }, cooldown: 2, minLevel: 4, tier: 2,
    unlock: { stats: { INT: 24 }, affinities: { water: 2 } },
    desc: 'La pression des profondeurs jaillit sous la cible.' }),
  spell({ id: 'vt_lance_gaia', name: 'Lance de Gaïa', icon: '🗻', type: 'attack', element: 'earth',
    affinity: { INT: 1.5 }, cost: { mp: 16 }, cooldown: 3, minLevel: 5, tier: 2,
    statuses: [{ id: 'defDown', chance: 0.3, duration: 2 }],
    unlock: { stats: { INT: 26 }, affinities: { earth: 2 } },
    desc: 'Un pieu de roche vive transperce depuis le sol.' }),
  spell({ id: 'vt_marque_cendre', name: 'Marque de Cendre', icon: '🔥', type: 'debuff', element: 'fire',
    affinity: { INT: 0.7 }, cost: { mp: 16 }, cooldown: 3, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'fireBrand', chance: 0.9, duration: 3 }],
    unlock: { stats: { INT: 28 }, affinities: { fire: 3 } },
    desc: 'Grave une marque qui EXPOSE la cible au Feu — prépare le combo.' }),
  spell({ id: 'vt_prison_eau', name: 'Prison d\'Eau', icon: '🫧', type: 'debuff', element: 'water',
    affinity: { INT: 0.9 }, cost: { mp: 20 }, cooldown: 4, minLevel: 7, tier: 3, branch: 'control',
    statuses: [{ id: 'freeze', chance: 1, duration: 2 }],
    unlock: { stats: { INT: 28 }, affinities: { water: 3 } },
    desc: 'Une sphère liquide enserre la cible et fige ses gestes.' }),
  spell({ id: 'vt_brume_deletere', name: 'Brume Délétère', icon: '☁️', type: 'aoe', element: 'nature',
    affinity: { INT: 1.0 }, cost: { mp: 24 }, target: 'allEnemies', cooldown: 4, minLevel: 8, tier: 3,
    statuses: [{ id: 'poison', chance: 0.7, duration: 3 }],
    unlock: { stats: { INT: 32 }, affinities: { nature: 4 } },
    desc: 'Un brouillard verdâtre qui ronge tous les poumons.' }),
  spell({ id: 'vt_supernova', name: 'Supernova', icon: '💥', type: 'aoe', element: 'fire',
    affinity: { INT: 2.2 }, cost: { mp: 42 }, target: 'allEnemies', cooldown: 6, minLevel: 12, tier: 4,
    statuses: [{ id: 'burn', chance: 0.6, duration: 3 }, { id: 'stun', chance: 0.3, duration: 1 }],
    unlock: { stats: { INT: 44 }, affinities: { fire: 6 }, skills: ['vt_marque_cendre'] },
    desc: 'Une étoile naît et meurt au milieu du champ : l\'onde de choc peut sonner les survivants.' }),

  // ═══ ⚕️ ÉCOLE DE LA FOI — protections sacrées ═══
  spell({ id: 'vt_ward_sacre', name: 'Rempart de Lumière', icon: '🕯️', type: 'buff', element: 'holy',
    affinity: { SAG: 0.4 }, cost: { mp: 16 }, target: 'allAllies', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'holyWard', chance: 1, duration: 3 }],
    unlock: { stats: { SAG: 24 }, resistances: { holy: 2 } },
    desc: 'La lumière elle-même refuse de blesser les protégés.' }),
  spell({ id: 'vt_ward_ombre', name: 'Voile Contre-Ténèbres', icon: '🕯️', type: 'buff', element: 'dark',
    affinity: { SAG: 0.4 }, cost: { mp: 16 }, target: 'allAllies', cooldown: 4, minLevel: 5, tier: 2, branch: 'defense',
    statuses: [{ id: 'darkWard', chance: 1, duration: 3 }],
    unlock: { stats: { SAG: 24 }, resistances: { dark: 2 } },
    desc: 'Un voile béni que les ombres ne peuvent franchir.' }),

  // ═══ 🎵 ÉCOLE DU CHANT — répertoire élargi ═══
  spell({ id: 'vt_berceuse', name: 'Berceuse', icon: '🎼', type: 'debuff', element: 'wind',
    affinity: { CHA: 1.0 }, cost: { mp: 16 }, cooldown: 4, minLevel: 3, tier: 2, branch: 'control',
    statuses: [{ id: 'sleep', chance: 0.6, duration: 2 }],
    unlock: { stats: { CHA: 20 } },
    desc: 'Trois notes suffisent à fermer les paupières.' }),
  spell({ id: 'vt_melodie_regen', name: 'Mélodie Régénérante', icon: '🎶', type: 'buff', element: 'water',
    affinity: { CHA: 0.6 }, cost: { mp: 18 }, target: 'allAllies', cooldown: 4, minLevel: 4, tier: 2, branch: 'support',
    statuses: [{ id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { CHA: 22 }, affinities: { water: 1 } },
    desc: 'Un air doux qui recoud les plaies mesure après mesure.' }),
  spell({ id: 'vt_note_stridente', name: 'Note Stridente', icon: '📣', type: 'debuff', element: 'wind',
    affinity: { CHA: 0.8 }, cost: { mp: 18 }, target: 'allEnemies', cooldown: 4, minLevel: 5, tier: 2, branch: 'control',
    statuses: [{ id: 'atkDown', chance: 0.6, duration: 2 }],
    unlock: { stats: { CHA: 24 }, affinities: { wind: 2 } },
    desc: 'Une dissonance qui fait grincer toutes les dents ennemies.' }),
  spell({ id: 'vt_chant_sirene', name: 'Chant de la Sirène', icon: '🧜', type: 'debuff', element: 'water',
    affinity: { CHA: 1.2 }, cost: { mp: 22 }, cooldown: 5, minLevel: 6, tier: 3, branch: 'control',
    statuses: [{ id: 'charmed', chance: 0.6, duration: 2 }],
    unlock: { stats: { CHA: 26 }, affinities: { water: 2 } },
    desc: 'Le chant des profondeurs égare les cœurs.' }),
  spell({ id: 'vt_requiem', name: 'Requiem', icon: '🎻', type: 'attack', element: 'dark',
    affinity: { CHA: 1.5 }, cost: { mp: 22 }, cooldown: 3, minLevel: 7, tier: 3,
    unlock: { stats: { CHA: 28 }, affinities: { dark: 3 } },
    desc: 'Un chant funèbre qui pèse sur l\'âme comme une dalle.' }),
  spell({ id: 'vt_hymne_courage', name: 'Hymne du Courage', icon: '🎺', type: 'buff', element: 'holy',
    affinity: { CHA: 0.6 }, cost: { mp: 24 }, target: 'allAllies', cooldown: 5, minLevel: 8, tier: 3, branch: 'support',
    statuses: [{ id: 'atkUp', chance: 1, duration: 3 }, { id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { CHA: 30 }, affinities: { holy: 2 } },
    desc: 'L\'hymne des héros anciens gonfle tous les cœurs.' }),
  spell({ id: 'vt_ovation', name: 'Ovation', icon: '👏', type: 'buff', element: 'physical',
    affinity: { CHA: 0.6 }, cost: { mp: 26 }, target: 'allAllies', cooldown: 5, minLevel: 10, tier: 4, branch: 'support',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }, { id: 'intUp', chance: 1, duration: 3 }],
    unlock: { stats: { CHA: 34 } },
    desc: 'La foule invisible applaudit — l\'équipe se surpasse.' }),
  spell({ id: 'vt_opera_final', name: 'Opéra Final', icon: '🎭', type: 'aoe', element: 'holy',
    affinity: { CHA: 2.0 }, cost: { mp: 38 }, target: 'allEnemies', cooldown: 6, minLevel: 12, tier: 4,
    statuses: [{ id: 'stun', chance: 0.25, duration: 1 }],
    unlock: { stats: { CHA: 40 }, affinities: { holy: 5 }, skills: ['vt_hymne_courage'] },
    desc: 'Le dernier acte : un crescendo qui foudroie la scène entière.' }),

  // ═══ 🔄 CONTRES — durée courte, cooldown long : à PLACER, pas à spammer ═══
  spell({ id: 'vt_riposte', name: 'Posture de Riposte', icon: '🛡️', type: 'buff', element: 'physical',
    affinity: { END: 0.5 }, cost: { stamina: 16 }, target: 'self', cooldown: 5, minLevel: 6, tier: 3, branch: 'defense',
    statuses: [{ id: 'physCounter', chance: 1, duration: 2 }],
    unlock: { stats: { END: 26 }, skills: ['vt_carapace'] },
    desc: 'Encaisse en angle : chaque coup physique repart vers son auteur.' }),
  spell({ id: 'vt_miroir_sorts', name: 'Miroir de Sorts', icon: '🪞', type: 'buff', element: 'physical',
    affinity: { SAG: 0.5 }, cost: { mp: 20 }, target: 'ally', cooldown: 5, minLevel: 7, tier: 3, branch: 'defense',
    statuses: [{ id: 'magicCounter', chance: 1, duration: 2 }],
    unlock: { stats: { SAG: 28, ESP: 20 } },
    desc: 'Le soutien lit le monstre lanceur : un miroir se dresse devant l\'allié visé.' }),
  spell({ id: 'vt_contre_charge', name: 'Contre-Charge', icon: '⚔️', type: 'buff', element: 'physical',
    affinity: { FOR: 0.5 }, cost: { stamina: 20 }, target: 'self', cooldown: 5, minLevel: 8, tier: 3, branch: 'defense',
    statuses: [{ id: 'physCounter', chance: 1, duration: 2 }, { id: 'atkUp', chance: 1, duration: 2 }],
    unlock: { stats: { FOR: 28 }, skills: ['g_stance'] },
    desc: 'Absorber la charge, rendre la douleur — avec intérêts.' }),
  spell({ id: 'vt_echo_douloureux', name: 'Écho Douloureux', icon: '📿', type: 'buff', element: 'physical',
    affinity: { ESP: 0.5 }, cost: { mp: 24 }, target: 'self', cooldown: 5, minLevel: 9, tier: 3, branch: 'defense',
    statuses: [{ id: 'magicCounter', chance: 1, duration: 2 }],
    unlock: { stats: { ESP: 32 }, skills: ['vt_meditation'] },
    desc: 'L\'esprit renvoie chaque sort hostile comme un écho.' }),
  spell({ id: 'vt_loi_talion', name: 'Loi du Talion', icon: '⚖️', type: 'buff', element: 'holy',
    affinity: { SAG: 0.5 }, cost: { mp: 36 }, target: 'allAllies', cooldown: 6, minLevel: 12, tier: 4, branch: 'defense',
    statuses: [{ id: 'fullCounter', chance: 1, duration: 2 }],
    unlock: { stats: { SAG: 38, END: 30 }, titles: ['veteran'] },
    desc: 'Œil pour œil : toute l\'équipe rend chaque coup reçu. Réservé aux vétérans.' }),

  // ═══ ⚗️ FUSIONS CROSS-ÉCOLES — « tu débloques tel ET tel sort, paf : celui-là » ═══
  spell({ id: 'vt_vapeur', name: 'Vapeur Brûlante', icon: '♨️', type: 'aoe', element: 'water',
    affinity: { INT: 1.4 }, cost: { mp: 28 }, target: 'allEnemies', cooldown: 4, minLevel: 8, tier: 3,
    statuses: [{ id: 'burn', chance: 0.5, duration: 3 }],
    unlock: { stats: { INT: 30 }, affinities: { fire: 3, water: 3 }, skills: ['m_fireball', 'vt_geyser'] },
    desc: 'Feu + Eau : un nuage bouillant qui ébouillante tous les rangs.' }),
  spell({ id: 'vt_verglas', name: 'Verglas Traître', icon: '🧊', type: 'attack', element: 'earth',
    affinity: { INT: 1.5 }, cost: { mp: 24 }, cooldown: 4, minLevel: 8, tier: 3,
    statuses: [{ id: 'freeze', chance: 1, duration: 2 }],
    unlock: { stats: { INT: 28 }, affinities: { ice: 3, earth: 2 }, skills: ['vt_givre', 'vt_lance_gaia'] },
    desc: 'Glace + Terre : le sol se change en patinoire mortelle.' }),
  spell({ id: 'vt_eclipse', name: 'Éclipse', icon: '🌗', type: 'attack', element: 'dark',
    affinity: { SAG: 1.2, ESP: 0.8 }, cost: { mp: 32 }, cooldown: 5, minLevel: 10, tier: 4,
    statuses: [{ id: 'stun', chance: 0.4, duration: 1 }],
    unlock: { stats: { SAG: 32, ESP: 28 }, affinities: { holy: 3, dark: 3 }, skills: ['p_smite', 'vt_onde_psychique'] },
    desc: 'Sacré + Ténèbres : l\'instant impossible où les deux se touchent.' }),
  spell({ id: 'vt_chant_lames', name: 'Chant des Lames', icon: '🗡️', type: 'buff', element: 'physical',
    affinity: { CHA: 0.5 }, cost: { mp: 26 }, target: 'allAllies', cooldown: 5, minLevel: 9, tier: 3, branch: 'support',
    statuses: [{ id: 'atkUp', chance: 1, duration: 3 }, { id: 'regen', chance: 1, duration: 3 }],
    unlock: { stats: { CHA: 26, FOR: 22 }, skills: ['b_heroic', 'vt_cri_antique'] },
    desc: 'Chant + Force : la ballade soutient les bras ET referme les plaies.' }),
  spell({ id: 'vt_orage_cendres', name: 'Orage de Cendres', icon: '🌋', type: 'aoe', element: 'fire',
    affinity: { AGI: 1.0, INT: 0.6 }, cost: { mp: 30 }, target: 'allEnemies', cooldown: 5, minLevel: 9, tier: 3,
    statuses: [{ id: 'atkDown', chance: 0.6, duration: 2 }],
    unlock: { stats: { AGI: 28, INT: 24 }, affinities: { fire: 2, wind: 2 }, skills: ['vt_frappe_sonique', 'vt_cendres'] },
    desc: 'Vent + Feu : des cendres ardentes portées par la bourrasque.' }),
  spell({ id: 'vt_deux_poisons', name: 'Lame des Deux Poisons', icon: '🐍', type: 'attack', element: 'ice',
    affinity: { DEX: 1.5 }, cost: { stamina: 22 }, cooldown: 4, minLevel: 9, tier: 3,
    statuses: [{ id: 'poison', chance: 0.8, duration: 3 }, { id: 'freeze', chance: 0.4, duration: 2 }],
    unlock: { stats: { DEX: 30 }, affinities: { ice: 2, nature: 2 }, skills: ['vt_piqure', 'vt_lame_gel'] },
    desc: 'Glace + Venin : le froid fige, le poison achève.' }),
  spell({ id: 'vt_symphonie', name: 'Symphonie Élémentaire', icon: '🎼', type: 'buff', element: 'physical',
    affinity: { CHA: 0.6 }, cost: { mp: 40 }, target: 'allAllies', cooldown: 6, minLevel: 12, tier: 4, branch: 'support',
    statuses: [{ id: 'allAttune', chance: 1, duration: 2 }],
    unlock: { stats: { CHA: 36, ESP: 30 }, skills: ['b_war_cry', 'vt_harmonie_sauvage'] },
    desc: 'Chant + Esprit : toute l\'équipe résonne avec TOUS les éléments.' }),

  // ═══ 🏅 SORTS À TITRE — les hauts faits ouvrent des pouvoirs uniques ═══
  spell({ id: 'vt_plume_phenix', name: 'Plume de Phénix', icon: '🪶', type: 'revive', element: 'fire',
    affinity: { VIT: 1.2 }, cost: { mp: 40 }, target: 'ally', effect: { pctHp: 0.4 }, cooldown: 6, minLevel: 10, tier: 4, branch: 'support',
    unlock: { stats: { VIT: 30 }, affinities: { fire: 4 }, titles: ['phoenix_slayer'] },
    desc: 'Qui a vaincu le phénix a appris son secret : renaître des cendres.' }),
  spell({ id: 'vt_colere_kraken', name: 'Colère du Kraken', icon: '🐙', type: 'aoe', element: 'water',
    affinity: { FOR: 1.6 }, cost: { stamina: 28 }, target: 'allEnemies', cooldown: 5, minLevel: 10, tier: 4,
    statuses: [{ id: 'freeze', chance: 0.4, duration: 2 }],
    unlock: { stats: { FOR: 34 }, affinities: { water: 4 }, titles: ['kraken_bane'] },
    desc: 'Des tentacules d\'eau noire écrasent tout le champ de bataille.' }),
  spell({ id: 'vt_fleau_lich', name: 'Fléau du Lich', icon: '💀', type: 'attack', element: 'dark',
    affinity: { INT: 1.7 }, cost: { mp: 30 }, cooldown: 5, minLevel: 11, tier: 4,
    statuses: [{ id: 'darkBrand', chance: 1, duration: 3 }],
    unlock: { stats: { INT: 38 }, affinities: { dark: 5 }, titles: ['lich_slayer'] },
    desc: 'Le sort favori du Lich, arraché à son grimoire : frappe ET expose aux Ténèbres.' }),
  spell({ id: 'vt_pas_duelliste', name: 'Pas du Duelliste', icon: '🤺', type: 'buff', element: 'physical',
    affinity: { DEX: 0.5 }, cost: { stamina: 18 }, target: 'self', cooldown: 5, minLevel: 8, tier: 3, branch: 'defense',
    statuses: [{ id: 'physCounter', chance: 1, duration: 2 }, { id: 'vitUp', chance: 1, duration: 2 }],
    unlock: { stats: { DEX: 28 }, titles: ['duelist'] },
    desc: 'L\'art du duel : esquiver d\'un pas, répondre dans le même souffle.' }),
  spell({ id: 'vt_instinct_meute', name: 'Instinct de Meute', icon: '🐺', type: 'buff', element: 'physical',
    affinity: { AGI: 0.5 }, cost: { stamina: 24 }, target: 'allAllies', cooldown: 5, minLevel: 9, tier: 3, branch: 'support',
    statuses: [{ id: 'vitUp', chance: 1, duration: 3 }, { id: 'defUp', chance: 1, duration: 3 }],
    unlock: { stats: { AGI: 30 }, titles: ['pack_hunter'] },
    desc: 'Chasser ensemble : la meute couvre ses flancs et accélère.' }),
];

const LIFE_SPELLS_BY_ID: Record<string, any> = Object.fromEntries(LIFE_SPELLS.map(s => [s.id, s]));

// ── Arbre complet ────────────────────────────────────────────────────────

/** L'Arbre Mycélien complet : sorts hérités des 6 classes + Rameaux de Vie. */
export function getLifeTree(content?: LifeTreeContentInput): LifeNode[] {
  const { skillTrees, classes } = resolveLifeTreeContent(content);
  const nodes: LifeNode[] = [];
  for (const [classId, tree] of Object.entries(skillTrees)) {
    for (const skill of tree) {
      const unlock = deriveUnlock(skill, classId, classes);
      nodes.push({ skill, sourceClass: classId, voie: voieOf(unlock, skill, classId), unlock });
    }
  }
  for (const skill of LIFE_SPELLS) {
    const unlock: LifeUnlock = { level: skill.minLevel || 1, ...(skill.unlock || {}) };
    nodes.push({ skill, sourceClass: null, voie: voieOf(unlock, skill, null), unlock });
  }
  return nodes;
}

/** Cherche un sort par id dans TOUT l'arbre (classes + Rameaux). */
export function findSkillAnywhere(skillId: string, skillTrees: Record<string, any[]> = SKILL_TREES as Record<string, any[]>) {
  if (LIFE_SPELLS_BY_ID[skillId]) return LIFE_SPELLS_BY_ID[skillId];
  for (const tree of Object.values(skillTrees)) {
    const found = tree.find((s: any) => s.id === skillId);
    if (found) return found;
  }
  return null;
}

// ── Descriptions précises (data-driven) ──────────────────────────────────
// Les libellés sont générés depuis les VRAIES données de statuts : chance,
// durée, et effet exact (« −20% à TOUS les dégâts », « +50% dégâts de Feu
// uniquement »…). Plus de « sape son attaque » ambigu.

export const describeStatusEntry = describeCombatStatusEntry;
export const describeSkillStatuses = describeCombatSkillStatuses;

// ── Évaluateur ───────────────────────────────────────────────────────────

/** Évalue un prérequis. Retourne ok + la liste des manques (libellés français). */
export function evaluateUnlock(
  unlock: LifeUnlock,
  ctx: UnlockContext,
  content?: LifeTreeContentInput
): { ok: boolean; missing: UnlockMissing[] } {
  const missing: UnlockMissing[] = [];

  if (unlock.level && ctx.level < unlock.level) {
    missing.push({ kind: 'level', label: `Niveau ${unlock.level} requis (niv. ${ctx.level})`, have: ctx.level, need: unlock.level });
  }
  for (const [stat, need] of Object.entries(unlock.stats || {})) {
    const have = Math.floor(ctx.stats[stat] || 0);
    if (have < need) {
      missing.push({ kind: 'stat', label: `${stat} ${need} requis (${have})`, have, need });
    }
  }
  for (const [el, need] of Object.entries(unlock.affinities || {})) {
    const have = ctx.stats[`affinity_${el}`] || 0;
    if (have < need) {
      const elName = (ELEMENTS as any)[el]?.name || el;
      const elIcon = (ELEMENTS as any)[el]?.icon || '';
      missing.push({ kind: 'affinity', label: `Affinité ${elName} ${elIcon} ${need} requise (${have.toFixed(1)})`, have, need });
    }
  }
  for (const [el, need] of Object.entries(unlock.resistances || {})) {
    const have = ctx.stats[`resistance_${el}`] || 0;
    if (have < need) {
      const elName = (ELEMENTS as any)[el]?.name || el;
      const elIcon = (ELEMENTS as any)[el]?.icon || '';
      missing.push({ kind: 'resistance', label: `Résistance ${elName} ${elIcon} ${need} requise (${have.toFixed(1)})`, have, need });
    }
  }
  for (const skillId of unlock.skills || []) {
    if (!ctx.skillsUnlocked.includes(skillId)) {
      const name = findSkillAnywhere(skillId, content?.skillTrees)?.name || skillId;
      missing.push({ kind: 'skill', label: `Requiert : ${name}`, have: false, need: true, skillId });
    }
  }
  for (const titleId of unlock.titles || []) {
    if (!ctx.titles.includes(titleId)) {
      const t = (TITLES as any)[titleId];
      missing.push({ kind: 'title', label: `Titre requis : ${t?.icon || '🏅'} ${t?.name || titleId}`, have: false, need: true });
    }
  }

  return { ok: missing.length === 0, missing };
}
